import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { LayoutDashboard, MessageCircleHeart, Dumbbell, ScanLine, Sprout, User, LogOut, Leaf, Sun, Moon } from "lucide-react";
import FloatingGracely from "./FloatingGracely";

export default function AppShell() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const items = [
    { to: "/dashboard", icon: LayoutDashboard, label: lang === "id" ? "Dashboard" : "Dashboard", testId: "nav-dashboard" },
    { to: "/chat", icon: MessageCircleHeart, label: lang === "id" ? "Percakapan" : "Conversation", testId: "nav-chat" },
    { to: "/workouts", icon: Dumbbell, label: t("workouts_title"), testId: "nav-workouts" },
    { to: "/scan", icon: ScanLine, label: t("food_title"), testId: "nav-scan" },
    { to: "/habits", icon: Sprout, label: t("habits_title"), testId: "nav-habits" },
    { to: "/profile", icon: User, label: t("profile_title"), testId: "nav-profile" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-stone-200 bg-white sticky top-0 h-screen">
        <div className="p-6 border-b border-stone-200 flex items-center gap-2">
          <img src="/logo.png" alt="Gracely" className="w-9 h-9 object-contain" />
          <span className="font-serif-display text-2xl text-stone-900">Gracely</span>
        </div>
        <nav className="p-4 flex-1 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? "bg-[#EAF0E4] text-[#4A5D23] font-semibold" : "text-stone-600 hover:bg-stone-50"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-stone-200 space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="flex-1 px-3 py-2 rounded-full text-xs font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 btn-pop"
              data-testid="lang-toggle-sidebar"
            >
              {lang === "id" ? "ID · EN" : "EN · ID"}
            </button>
            <button
              onClick={toggle}
              className="w-9 h-9 rounded-full text-stone-700 bg-stone-100 hover:bg-stone-200 btn-pop flex items-center justify-center"
              aria-label="Toggle theme"
              data-testid="theme-toggle-sidebar"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-[#4A5D23] text-white flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]?.toUpperCase() || "G"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{user?.email}</p>
            </div>
            <button onClick={() => { nav("/"); logout(); }} className="text-stone-500 hover:text-[#E07A5F]" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 glass-nav">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Gracely" className="w-7 h-7 object-contain" />
            <span className="font-serif-display text-xl">Gracely</span>
          </div>
          <button onClick={() => { nav("/"); logout(); }} className="text-stone-500" data-testid="logout-btn-mobile">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="flex overflow-x-auto gap-1 px-2 pb-2">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-[#4A5D23] text-white" : "bg-stone-100 text-stone-600"}`}>
              {it.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 pt-28 md:pt-0 md:p-10 p-4 max-w-7xl mx-auto w-full">
        <Outlet />
      </main>
      <FloatingGracely />
    </div>
  );
}
